import type { ComponentType, ReactNode, SVGProps } from "react";
import { useMatch } from "react-router";
import { useTranslations } from "@acto/web-common/locales";
import {
  CalendarDaysFilled,
  CalendarDaysOutline,
  CheckSquareRounded,
  CheckSquareRoundedFilled,
  HouseFilled,
  HouseOutline,
  Logo,
  UsersFilled,
  UsersOutline,
} from "@acto/web-common/components";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { useOpenCustomerChat } from "@acto/taskpane/field-sales-2/utils/useOpenCustomerChat";
import {
  BottomNavCircleButton,
  BottomNavShell,
  BottomNavTabBar,
  BottomNavTabRow,
  type BottomNavTab,
} from "./components/patterns/BottomNav";

interface HomeBottomNavProps {
  /** Optional pill rendered above the tab row (e.g. the Todos page's "+ Neue Aufgabe"). */
  actionPill?: ReactNode;
}

interface NavIconPair {
  Outline: ComponentType<SVGProps<SVGSVGElement>>;
  Filled: ComponentType<SVGProps<SVGSVGElement>>;
}

// Every pair lives on the shared 24-grid (see the web-common icon rules), so all tabs
// render at one fixed size and the outline/filled swap cannot resize the glyph.
const NAV_ICON_SIZE = 26;

const NAV_ICONS: Record<"home" | "todos" | "calendar" | "customers", NavIconPair> = {
  home: { Outline: HouseOutline, Filled: HouseFilled },
  todos: { Outline: CheckSquareRounded, Filled: CheckSquareRoundedFilled },
  calendar: { Outline: CalendarDaysOutline, Filled: CalendarDaysFilled },
  customers: { Outline: UsersOutline, Filled: UsersFilled },
};

function navIcons({ Outline, Filled }: NavIconPair): Pick<BottomNavTab, "icon" | "activeIcon"> {
  const iconProps = {
    width: NAV_ICON_SIZE,
    height: NAV_ICON_SIZE,
    "aria-hidden": true,
    style: { flexShrink: 0 },
  } as const;
  return {
    icon: <Outline {...iconProps} />,
    activeIcon: <Filled {...iconProps} />,
  };
}

export function HomeBottomNav({ actionPill }: HomeBottomNavProps = {}) {
  const t = useTranslations("fieldSalesTwo.bottomNav");
  const openChat = useOpenCustomerChat(undefined);

  const homeMatch = useMatch(ActoRoutes.HOME);
  const todosMatch = useMatch(ActoRoutes.TODOS_OVERVIEW);
  const calendarMatch = useMatch(ActoRoutes.CALENDAR);
  const customersMatch = useMatch(ActoRoutes.CUSTOMERS);

  const tabs: BottomNavTab[] = [
    {
      key: "home",
      label: t("home"),
      ...navIcons(NAV_ICONS.home),
      to: ActoRoutes.HOME,
      isActive: Boolean(homeMatch),
    },
    {
      key: "calendar",
      label: t("calendar"),
      ...navIcons(NAV_ICONS.calendar),
      to: ActoRoutes.CALENDAR,
      isActive: Boolean(calendarMatch),
    },
    {
      key: "todos",
      label: t("tasks"),
      ...navIcons(NAV_ICONS.todos),
      to: ActoRoutes.TODOS_OVERVIEW,
      isActive: Boolean(todosMatch),
    },
    {
      key: "customers",
      label: t("customers"),
      ...navIcons(NAV_ICONS.customers),
      to: ActoRoutes.CUSTOMERS,
      isActive: Boolean(customersMatch),
    },
  ];

  return (
    <BottomNavShell>
      {actionPill}
      <BottomNavTabRow>
        <BottomNavTabBar tabs={tabs} />
        <BottomNavCircleButton
          icon={<Logo width={24} height={24} aria-hidden="true" style={{ flexShrink: 0 }} />}
          onClick={openChat}
          aria-label={t("openChat")}
        />
      </BottomNavTabRow>
    </BottomNavShell>
  );
}
